import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../config/config";
import { ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";

const DrivingChoices = () => {
  const navigate = useNavigate();

  const carTypes = ["SUV", "Sedan", "Hatchback", "Luxury"];
  const cities = [
    "Hyderabad",
    "Bangalore",
    "Delhi",
    "Mumbai",
    "Chennai",
    "Pune",
    "Ahmedabad",
    "Kolkata",
    "Surat",
    "Jaipur",
  ];

  const [formData, setFormData] = useState({
    tripType: "",
    carTransmission: "",
    carType: [],
    city: "",
    area: "",
  });

  useEffect(() => {
    const fetchDrivingChoices = async () => {
      const token = localStorage.getItem("userPublicToken");
      try {
        const res = await fetch(API.ADMIN.USERDASHBOARD, {
          headers: { Authorization: "Bearer " + token },
        });
        const data = await res.json();
        const choices = data?.data?.drivingChoices;

        if (choices) {
          setFormData({
            tripType: choices.tripType || "",
            carTransmission: choices.carTransmission || "",
            carType: Array.isArray(choices.carType)
              ? choices.carType
              : typeof choices.carType === "string"
              ? choices.carType === "all"
                ? [...carTypes]
                : choices.carType.split(",")
              : [],
            city: choices.city || "",
            area: choices.area || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch driving choices:", err);
      }
    };

    fetchDrivingChoices();
  }, []);

  const handleRadioChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleCarTypeChange = (type) => {
    let updated = [...formData.carType];
    if (updated.includes(type)) {
      updated = updated.filter((t) => t !== type);
    } else {
      updated.push(type);
    }

    // If all selected manually, auto-select all
    if (updated.length === carTypes.length) {
      updated = [...carTypes];
    }

    setFormData({ ...formData, carType: updated });
  };

  const handleAllToggle = () => {
    if (formData.carType.length === carTypes.length) {
      setFormData({ ...formData, carType: [] });
    } else {
      setFormData({ ...formData, carType: [...carTypes] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("userPublicToken");

    const requiredFields = [
      { field: formData.tripType, label: "Trip Type" },
      { field: formData.carTransmission, label: "Car Transmission" },
      { field: formData.carType.length > 0, label: "Car Types" },
      { field: formData.city, label: "City" },
      { field: formData.area, label: "Area" },
    ];

    for (const { field, label } of requiredFields) {
      if (!field) {
        toast.error(`Please select/enter ${label}`);
        return;
      }
    }

    try {
      const res = await fetch(API.ADMIN.SAVE_DRIVING_CHOICES, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();
      if (result.success) {
        toast.success("Driving choices saved!");
        navigate("/user-dashboard");
      } else {
        toast.error(result.message || "Failed to save choices");
      }
    } catch (err) {
      console.error("Error submitting:", err);
      toast.error("An error occurred");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-8 space-y-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-indigo-600"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            <span>Back</span>
          </button>
          <h2 className="text-xl font-bold text-indigo-700">
            Driving Preferences
          </h2>
        </div>

        {/* Trip Type */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            Trip Type
          </label>
          <div className="flex flex-wrap gap-4">
            {["in_city", "outstation", "both"].map((type) => (
              <label
                key={type}
                className="flex items-center gap-2 text-gray-800"
              >
                <input
                  type="radio"
                  name="tripType"
                  value={type}
                  onChange={() => handleRadioChange("tripType", type)}
                  checked={formData.tripType === type}
                  className="accent-indigo-600"
                />
                <span className="capitalize">{type.replace("_", " ")}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Transmission */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            Car Transmission
          </label>
          <div className="flex flex-wrap gap-4">
            {["Manual", "Manual and Automatic"].map((type) => (
              <label
                key={type}
                className="flex items-center gap-2 text-gray-800"
              >
                <input
                  type="radio"
                  name="carTransmission"
                  value={type}
                  onChange={() => handleRadioChange("carTransmission", type)}
                  checked={formData.carTransmission === type}
                  className="accent-indigo-600"
                />
                <span className="capitalize">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Car Types */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            Car Types
          </label>
          <div className="flex flex-wrap gap-4">
            {carTypes.map((type) => (
              <label
                key={type}
                className="flex items-center gap-2 text-gray-800"
              >
                <input
                  type="checkbox"
                  value={type}
                  checked={formData.carType.includes(type)}
                  onChange={() => handleCarTypeChange(type)}
                  className="accent-indigo-600"
                />
                <span className="capitalize">{type}</span>
              </label>
            ))}
            <label className="flex items-center gap-2 text-gray-800">
              <input
                type="checkbox"
                value="all"
                checked={formData.carType.length === carTypes.length}
                onChange={handleAllToggle}
                className="accent-indigo-600"
              />
              All
            </label>
          </div>
        </div>

        {/* City */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            City You Want to Drive In
          </label>
          <select
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            required
            className="w-full border rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="">Select City</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>

        {/* Area */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            Area You Want to Drive From <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.area}
            onChange={(e) => setFormData({ ...formData, area: e.target.value })}
            placeholder="Enter Area"
            className="w-full border rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            required
          />
        </div>

        {/* Submit */}
        <div className="pt-4">
          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg shadow-md transition"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default DrivingChoices;
